import { useCallback, useEffect, useMemo, useState } from "react";
import { UseAllAssetsArgs } from "domains/assets/hooks/useAllAssets";
import { useAssetsByIds } from "domains/assets/hooks/useAssetsByIds";
import { mapAssetsToJobs } from "domains/assets/utils/mapAssetsToJobs";
import { useDebounce } from "domains/commons/hooks/useDebounce";
import useDeepMemo from "domains/commons/hooks/useDeepMemo";
import {
  mapAssetsToCanvasFiles,
  mapAssetsToImagesFiles,
  mapModelsToFiles,
} from "domains/file-manager/interfaces";
import { useScenarioToast } from "domains/notification/hooks/useScenarioToast";
import {
  ALLOWED_RESTRICT_VALUES,
  RestrictOption,
} from "domains/search/components/SearchModal/FilterRestricts";
import { useSearchContext } from "domains/search/contexts/SearchProvider";
import { MeilisearchResult } from "domains/search/interfaces/Search";
import { avg } from "domains/search/utils/math";
import { useTeamContext } from "domains/teams/contexts/TeamProvider";
import {
  GetAssetsByAssetIdApiResponse,
  GetModelsByModelIdApiResponse,
  useGetAssetsByAssetIdQuery,
} from "infra/api/generated/api";
import _ from "lodash";
import moment from "moment";

import { skipToken } from "@reduxjs/toolkit/query";

interface UseSearchArgs {
  query?: string;
  assetId?: string;
  filters?: string;
  authorId?: string;
  dateRange?: { start: string; end: string | undefined };
  isHighResThumbnailRequired?: boolean;
  aiBoost?: boolean;
  facets?: string[];
  sort?: string;
  tags?: string[];
  collectionIds?: string[];
  nsfw?: string[];
  restrictBy?: RestrictOption[];
  privacy?: ("public" | "private")[] | "public" | "private";
  isEmptyQueryAllowed?: boolean;
}

interface UseAssetsSearchArgs
  extends Omit<UseSearchArgs, "facets" | "filters"> {
  types?: UseAllAssetsArgs["types"];
  styles?: string[];
  modelIds?: UseAllAssetsArgs["modelId"][];
}

interface UseModelsSearchArgs
  extends Omit<UseSearchArgs, "facets" | "filters"> {
  types?: GetModelsByModelIdApiResponse["model"]["type"][];
  sources?: GetModelsByModelIdApiResponse["model"]["source"][];
  conceptIds?: string[];
}

interface UseCanvasesSearchArgs
  extends Omit<UseSearchArgs, "facets" | "filters"> {}

const PAGE_SIZE = 50;

export function useAssetsSearch({
  modelIds: unmemoizedModelIds,
  types: unmemoizedTypes,
  styles: unmemoizedStyles,
  restrictBy: initialRestrictBy,
  ...props
}: UseAssetsSearchArgs) {
  const modelIds = useDeepMemo(unmemoizedModelIds);
  const types = useDeepMemo(unmemoizedTypes);
  const styles = useDeepMemo(unmemoizedStyles);

  const filters = useMemo(() => {
    return _.compact([
      ...(types?.length
        ? [
            "(" +
              types.map((type) => `metadata.type = "${type}"`).join(" OR ") +
              ")",
          ]
        : []),
      ...(styles?.length
        ? [
            "(" +
              styles
                .map((style) => `metadata.style = "${style}"`)
                .join(" OR ") +
              ")",
          ]
        : []),
      ...(modelIds?.length
        ? [
            "(" +
              modelIds
                .map((modelId) => `metadata.modelId = "${modelId}"`)
                .join(" OR ") +
              ")",
          ]
        : []),
    ]).join(" AND ");
  }, [types, styles, modelIds]);
  const restrictBy = useMemo(() => {
    return (initialRestrictBy ?? []).filter((option) =>
      ALLOWED_RESTRICT_VALUES["image"].includes(option)
    );
  }, [initialRestrictBy]);
  const facets = useMemo(
    () => [
      "metadata.type",
      "authorId",
      "tags",
      "collectionIds",
      "nsfw",
      "metadata.modelId",
    ],
    []
  );

  const { results, ...rest } = useSearch<
    GetAssetsByAssetIdApiResponse["asset"]
  >({
    ...props,
    filters,
    type: "asset",
    facets,
    restrictBy,
  });

  const { files, jobs } = useMemo(() => {
    const files = mapAssetsToImagesFiles(results);
    const jobs = mapAssetsToJobs(files);
    return { files, jobs };
  }, [results]);

  return {
    files,
    jobs,
    ...rest,
  };
}

export function useModelsSearch({
  types,
  sources,
  conceptIds,
  restrictBy: initialRestrictBy,
  ...props
}: UseModelsSearchArgs) {
  const filters = useMemo(() => {
    return [
      ...(types?.length
        ? ["(" + types.map((type) => `type = "${type}"`).join(" OR ") + ")"]
        : []),
      ...(sources?.length
        ? [
            "(" +
              sources.map((source) => `source = "${source}"`).join(" OR ") +
              ")",
          ]
        : []),
      ...(conceptIds?.length
        ? [
            "(" +
              conceptIds
                .map((conceptId) => `concepts.modelId = "${conceptId}"`)
                .join(" OR ") +
              ")",
          ]
        : []),
    ].join(" AND ");
  }, [types, sources, conceptIds]);
  const restrictBy = useMemo(() => {
    return (initialRestrictBy ?? []).filter((option) =>
      ALLOWED_RESTRICT_VALUES["model"].includes(option)
    );
  }, [initialRestrictBy]);
  const facets = useMemo(
    () => [
      "authorId",
      "tags",
      "collectionIds",
      "type",
      "source",
      "concepts.modelId",
    ],
    []
  );

  const { results, ...rest } = useSearch<
    GetModelsByModelIdApiResponse["model"]
  >({
    ...props,
    filters,
    type: "model",
    facets,
    restrictBy,
  });

  const files = useMemo(() => {
    return mapModelsToFiles(results);
  }, [results]);

  return {
    files,
    ...rest,
  };
}

export function useCanvasesSearch({
  restrictBy: initialRestrictBy,
  ...props
}: UseCanvasesSearchArgs) {
  const filters = useMemo(() => {
    return `metadata.type = canvas`;
  }, []);
  const restrictBy = useMemo(() => {
    let newRestrictBy = (initialRestrictBy ?? []).filter((option) =>
      ALLOWED_RESTRICT_VALUES["canvas"].includes(option)
    );
    if (newRestrictBy.includes("name")) {
      newRestrictBy = newRestrictBy.filter((option) => option !== "name");
      newRestrictBy.push("metadata.name");
    }
    return newRestrictBy;
  }, [initialRestrictBy]);
  const facets = useMemo(() => ["authorId", "tags", "collectionIds"], []);

  const { results, ...rest } = useSearch<
    GetAssetsByAssetIdApiResponse["asset"]
  >({
    ...props,
    filters,
    type: "asset",
    facets,
    restrictBy,
  });

  const files = useMemo(() => {
    return mapAssetsToCanvasFiles(results);
  }, [results]);

  return {
    files,
    ...rest,
  };
}

function useSearch<
  T extends
    | GetAssetsByAssetIdApiResponse["asset"]
    | GetModelsByModelIdApiResponse["model"]
>({
  query: untrimmedQuery,
  assetId,
  filters,
  type,
  dateRange,
  authorId,
  isHighResThumbnailRequired = true,
  aiBoost,
  facets,
  sort,
  tags,
  collectionIds,
  nsfw,
  restrictBy,
  privacy: unmemoizedPrivacy,
  isEmptyQueryAllowed = false,
}: UseSearchArgs & {
  type: T extends GetAssetsByAssetIdApiResponse["asset"] ? "asset" : "model";
}) {
  const privacy = useDeepMemo(unmemoizedPrivacy);

  const { getIndexes, searchClient, getQueryEmbedding, getIsPublicIndex } =
    useSearchContext();
  const { selectedProject } = useTeamContext();
  const { errorToast } = useScenarioToast();
  const [results, setResults] = useState<T[]>([]);
  const [resultCount, setResultCount] = useState<number | undefined>();
  const [resultsFacets, setResultsFacets] = useState<
    Record<string, Record<string, number>>
  >({});
  const [stopPaginationAt, setStopPaginationAt] = useState<
    | {
        [indexName: string]: number;
      }
    | undefined
  >(undefined);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [lastPageLoaded, setLastPageLoaded] = useState<number | undefined>(
    undefined
  );
  const indexes = useMemo(
    () =>
      getIndexes(type, {
        privacy: !privacy || Array.isArray(privacy) ? privacy : [privacy],
      }),
    [type, privacy, getIndexes]
  );
  const [deletedIds, setDeletedIds] = useState<string[]>([]);
  const [queryEmbedding, setQueryEmbedding] = useState<
    | {
        query: string;
        embedding: number[];
      }
    | undefined
  >(undefined);

  const { currentData: asset, error: assetError } = useGetAssetsByAssetIdQuery(
    assetId
      ? {
          projectId: selectedProject.id,
          assetId,
          withEmbedding: "true",
        }
      : skipToken
  );
  const instantQuery = useMemo(() => {
    return untrimmedQuery ? untrimmedQuery.trim() : untrimmedQuery;
  }, [untrimmedQuery]);
  const query = useDebounce(instantQuery, 500, {
    preventFor: (val) => !val,
  });

  const isDebouncing = query !== instantQuery && !query;

  const assetIds = useMemo(() => {
    if (!isHighResThumbnailRequired) return [];

    if (type === "asset") {
      return (results as GetAssetsByAssetIdApiResponse["asset"][]).map(
        (result) => result.id
      );
    }
    return (results as GetModelsByModelIdApiResponse["model"][])
      .map((result) => result.thumbnail?.assetId)
      .filter((value) => value !== undefined);
  }, [results, type, isHighResThumbnailRequired]);
  const { assetsByIds } = useAssetsByIds({
    assetIds,
    originalAssets: false,
  });

  const cleanedResults = useMemo(() => {
    if (type !== "asset") return results;
    const undefinedAssetIds = Object.keys(assetsByIds).filter(
      (key) => assetsByIds[key] === undefined
    );
    return results.filter((result) => !undefinedAssetIds.includes(result.id));
  }, [assetsByIds, results, type]);

  const cleanedCount = results.length - cleanedResults.length;

  const hasMore = useMemo(() => {
    if (!stopPaginationAt) {
      return true;
    }
    return Object.values(stopPaginationAt).some(
      (page) => page !== undefined && page > currentPage
    );
  }, [stopPaginationAt, currentPage]);

  const filteredResults = useMemo(() => {
    return cleanedResults.filter((result) => !deletedIds.includes(result.id));
  }, [cleanedResults, deletedIds]);

  const isAssetSearchNotReady = !!(
    assetId &&
    !asset?.asset.embedding &&
    !assetError
  );
  const isQuerySearchNotReady = !!(
    aiBoost &&
    query &&
    query !== queryEmbedding?.query
  );
  const isSearchNotReady = isAssetSearchNotReady || isQuerySearchNotReady;

  const isAssetSearchInvalid = !!(
    assetId &&
    !asset?.asset.embedding &&
    assetError
  );
  const isQuerySearchInvalid = !isEmptyQueryAllowed && !query && !assetId;
  const isSearchInvalid = isAssetSearchInvalid || isQuerySearchInvalid;

  // ----------------------------------

  const loadMore = useCallback(() => {
    if (!hasMore || lastPageLoaded === undefined) return;
    setCurrentPage(lastPageLoaded + 1);
  }, [hasMore, lastPageLoaded]);

  // ----------------------------------

  useEffect(() => {
    setStopPaginationAt(undefined);
    setCurrentPage(0);
    setLastPageLoaded(undefined);
  }, [
    query,
    asset,
    filters,
    dateRange,
    indexes,
    authorId,
    queryEmbedding,
    sort,
    facets,
    tags,
    collectionIds,
    nsfw,
    restrictBy,
    isEmptyQueryAllowed,
    aiBoost,
    assetId,
  ]);

  useEffect(() => {
    if (isSearchInvalid) {
      setResults([]);
      setResultsFacets({});
      setResultCount(undefined);
    }
  }, [isSearchInvalid]);

  useEffect(() => {
    if (!aiBoost || !query) {
      setQueryEmbedding(undefined);
      return;
    }

    if (query === queryEmbedding?.query) {
      return;
    }

    void getQueryEmbedding(query).then((embedding) => {
      setQueryEmbedding(embedding);
    });
  }, [query, getQueryEmbedding, queryEmbedding, aiBoost]);

  useEffect(() => {
    setResultsFacets({});
  }, [assetId, query, aiBoost]);

  useEffect(() => {
    if (!searchClient || currentPage === lastPageLoaded) return;
    if (isSearchNotReady || isSearchInvalid) return;

    let isAborted = false;
    const search = async () => {
      const searchQueries = indexes
        .map((indexName) => {
          if (
            indexName &&
            stopPaginationAt &&
            stopPaginationAt[indexName] <= currentPage
          ) {
            return undefined;
          }

          const withQueryEmbedding = aiBoost && queryEmbedding?.embedding;

          const meiliSearchParams: {
            attributesToSearchOn?: string[];
            vector?: number[];
            hybrid?: {
              embedder: string;
              semanticRatio: number;
            };
          } = {};

          if (restrictBy?.length) {
            meiliSearchParams.attributesToSearchOn = restrictBy;
          }

          if (
            asset?.asset.embedding &&
            withQueryEmbedding &&
            queryEmbedding?.embedding
          ) {
            // image search + text ai boost search
            meiliSearchParams.vector = avg(
              [asset.asset.embedding, queryEmbedding.embedding],
              [0.7, 0.3]
            );
            meiliSearchParams.hybrid = {
              embedder: "jina-clip-v2-1024",
              semanticRatio: 1,
            };
          } else if (asset?.asset.embedding) {
            // image search
            meiliSearchParams.vector = asset.asset.embedding;
            meiliSearchParams.hybrid = {
              embedder: "jina-clip-v2-1024",
              semanticRatio: query ? 0.8 : 1,
            };
          } else if (withQueryEmbedding && queryEmbedding?.embedding) {
            // text ai boost search
            meiliSearchParams.vector = queryEmbedding.embedding;
            meiliSearchParams.hybrid = {
              embedder: "jina-clip-v2-1024",
              semanticRatio: 0.8,
            };
          }

          let index = indexName;
          if (sort !== undefined && sort !== "relevance") {
            index = `${index}:${sort}`;
          } else if (
            sort === "relevance" &&
            !query &&
            !asset?.asset.embedding
          ) {
            index = `${index}:createdAtTS:desc`;
          }

          return {
            indexName: index,
            params: {
              facets,
              query: withQueryEmbedding ? undefined : query ?? "",
              filters: _.compact([
                getIsPublicIndex(index)
                  ? ""
                  : `ownerId = "${selectedProject.id}"`,
                filters,
                authorId ? `authorId = "${authorId}"` : "",
                dateRange?.start
                  ? `createdAtTS >= ${moment(
                      dateRange.start,
                      "YYYY-MM-DD"
                    ).unix()} AND createdAtTS <= ${moment(
                      dateRange.end || dateRange.start,
                      "YYYY-MM-DD"
                    )
                      .endOf("day")
                      .unix()}`
                  : "",
                tags?.length
                  ? "(" +
                    tags.map((tag) => `tags = "${tag}"`).join(" OR ") +
                    ")"
                  : "",
                collectionIds?.length
                  ? "(" +
                    collectionIds
                      .map(
                        (collectionId) => `collectionIds = "${collectionId}"`
                      )
                      .join(" OR ") +
                    ")"
                  : "",
                nsfw?.length
                  ? "(" +
                    nsfw.map((nsfw) => `nsfw = "${nsfw}"`).join(" OR ") +
                    ")"
                  : "",
              ]).join(" AND "),
              page: currentPage,
              hitsPerPage: PAGE_SIZE,
              meiliSearchParams,
            },
          };
        })
        .filter(Boolean);

      if (searchQueries.length === 0) return;

      let results: MeilisearchResult<T>[] = [];
      try {
        const response = await searchClient.search(searchQueries);
        results = response.results;
      } catch (error) {
        errorToast({
          title: "Error fetching search results",
          description: "Please try again later",
        });
        return;
      }

      if (isAborted) return;

      setStopPaginationAt((prevStopPaginationAt) => ({
        ...prevStopPaginationAt,
        ...results.reduce((acc, result) => {
          acc[result.index] = result.nbPages;
          return acc;
        }, {} as NonNullable<typeof stopPaginationAt>),
      }));
      if (currentPage === 0) {
        setResultCount(
          results.reduce((acc, result) => {
            return acc + result.nbHits;
          }, 0)
        );
      }
      setResults((prevResults) =>
        _.uniqBy(
          [
            ...(currentPage === 0 ? [] : prevResults),
            ...results
              .map((result) =>
                result.hits.map((hit) => ({
                  ...hit,
                  ...(type === "model"
                    ? {
                        id: hit.originalId ?? hit.id,
                        thumbnail:
                          "thumbnail" in hit && hit.thumbnail
                            ? {
                                assetId: hit.thumbnail.assetId,
                                url:
                                  assetsByIds[hit.thumbnail.assetId]?.meta
                                    .url ??
                                  `${process.env.NEXT_PUBLIC_CDN_URL}/thumbnails/${hit.thumbnail.assetId}`,
                              }
                            : undefined,
                        exampleAssetIds: [],
                        status: "trained",
                        trainingImages: [],
                      }
                    : {}),
                  ...(type === "asset"
                    ? assetsByIds[hit.id]
                      ? assetsByIds[hit.id]!.meta
                      : {
                          status: "success",
                          url: `${process.env.NEXT_PUBLIC_CDN_URL}/thumbnails/${hit.id}`,
                        }
                    : {}),
                }))
              )
              .flat(),
          ],
          "id"
        )
      );
      setResultsFacets((prevResultsFacets) => {
        results.forEach((result) => {
          Object.entries(result.facets).forEach(([key, value]) => {
            Object.keys(prevResultsFacets[key] ?? {}).forEach((facet) => {
              prevResultsFacets[key][facet] = 0;
            });
            Object.entries(value).forEach(([facet, count]) => {
              prevResultsFacets[key] = {
                ...prevResultsFacets[key],
                [facet]: count,
              };
            });
          });
        });
        return prevResultsFacets;
      });
      setLastPageLoaded(currentPage);
    };

    void search();

    return () => {
      isAborted = true;
    };
  }, [
    isSearchNotReady,
    isSearchInvalid,
    indexes,
    query,
    searchClient,
    stopPaginationAt,
    currentPage,
    lastPageLoaded,
    filters,
    authorId,
    type,
    dateRange,
    asset,
    assetId,
    assetsByIds,
    aiBoost,
    queryEmbedding,
    errorToast,
    facets,
    sort,
    tags,
    collectionIds,
    nsfw,
    restrictBy,
    selectedProject.id,
    getIsPublicIndex,
  ]);

  useEffect(() => {
    setResults((prevResults) =>
      prevResults.map((hit) => ({
        ...hit,
        ...("thumbnail" in hit && hit.thumbnail
          ? {
              thumbnail: {
                assetId: hit.thumbnail.assetId,
                url: assetsByIds[hit.thumbnail.assetId]?.meta.url,
              },
            }
          : {}),
        ...(type === "asset" && assetsByIds[hit.id]
          ? assetsByIds[hit.id]!.meta
          : {}),
      }))
    );
  }, [assetsByIds, type]);

  // ----------------------------------

  return {
    results: filteredResults,
    isLoading:
      (lastPageLoaded === undefined && !isSearchInvalid) || isDebouncing,
    hasMore,
    loadMore,
    resultCount: resultCount ? resultCount - cleanedCount : resultCount,
    facets: resultsFacets,
    setDeletedIds,
  };
}

// ------------------------------------

function isValidKey(key: string) {
  return (
    !key.endsWith("Id") &&
    !key.endsWith("Ids") &&
    key !== "id" &&
    key !== "createdAt" &&
    key !== "updatedAt" &&
    key !== "createdAtTS" &&
    key !== "updatedAtTS"
  );
}

export function useAutocompleteSearch({ query }: { query: string }) {
  const { getIndexes, searchClient } = useSearchContext();
  const [propositions, setPropositions] = useState<
    | {
        query: string;
        propositions: string[];
      }
    | undefined
  >();

  const indexes = useMemo(
    () => [...getIndexes("asset"), ...getIndexes("model")],
    [getIndexes]
  );

  const debouncedQuery = useDebounce(query, 100);
  const trimmedQuery = useMemo(() => {
    return debouncedQuery.trim();
  }, [debouncedQuery]);

  useEffect(() => {
    if (!searchClient) return;

    if (!trimmedQuery) {
      setPropositions(undefined);
      return;
    }

    let isAborted = false;

    const search = async () => {
      const response = await searchClient.search(
        indexes.map((indexName) => {
          return {
            indexName,
            params: {
              query: trimmedQuery,
              hitsPerPage: 5,
              highlightPreTag: "",
              highlightPostTag: "",
            },
          };
        })
      );
      const results: MeilisearchResult[] = response.results;

      if (isAborted) return;

      const regex = new RegExp(
        `\\b${_.escapeRegExp(trimmedQuery)}\\w*\\b`,
        "gi"
      );
      const newPropositions: string[] = [];
      results.forEach((result) => {
        result.hits.forEach((hit) => {
          Object.keys(hit._highlightResult).forEach((key) => {
            if (!isValidKey(key)) return;
            if (Array.isArray(hit._highlightResult[key])) {
              hit._highlightResult[key].forEach((x) => {
                if (!x) return;
                const value = typeof x === "object" ? x.value : x;
                if (!value) return;
                const matches = value.match(regex);
                if (matches) {
                  newPropositions.push(...matches);
                }
              });
              return;
            }
            const item = hit._highlightResult[key];
            if (!item.value && key !== "metadata") return;
            Object.keys(item).forEach((key2) => {
              if (!isValidKey(key2)) return;
              const x = item[key2 as keyof typeof item];
              if (!x) return;
              const value = typeof x === "object" ? x.value : x;
              if (!value) return;
              const matches = value.match(regex);
              if (matches) {
                newPropositions.push(...matches);
              }
            });
          });
        });
      });
      setPropositions({
        query: trimmedQuery,
        propositions: _.uniq(
          newPropositions.map((x) => x.toLowerCase())
        ).filter((x) => x.length > 1 && x !== trimmedQuery),
      });
    };

    void search();

    return () => {
      isAborted = true;
    };
  }, [trimmedQuery, searchClient, indexes]);

  return {
    propositions,
    isLoading: propositions === undefined && trimmedQuery.length > 0,
  };
}
